import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../api/client.js";
import { StepStrip } from "./ReviewsAutoRunBar.js";

/**
 * FIX-067 / owner t1149u: the Telegram daily publish status bar, at the top of the Review page under the auto-production bar — "the same status bar as the
 * videos have": the same card, the same step strip (`StepStrip`, shared with `ReviewsAutoRunBar`) and the same 15 s refresh, one strip per channel
 * (Emma's heaven, Yuna private): the channel name and its step row only (owner: no per-row status text, no switch note). Read-only: no button.
 *
 * Owner t1218u / t1219u: each channel behaves EXACTLY like the video bar — its own `card auto-run-bar`; the step strip ONLY while that channel's daily run is in
 * progress; otherwise ONE idle line in the video bar's idle markup ("No automatic run active" + muted "Last run: … · finished HH:MM", Israel time, the date in
 * front when not today — the video bar's rule). The last run comes from existing data only (the channel's latest publication, or today's failed run).
 *
 * Dashboard only, existing routes (no new endpoint or setting): `GET /images/:t/telegram-publish/status` (schedule, switches, channels, today's intake run),
 * `GET /images/:t/telegram-intake/posts` (today's LATEST request per channel — highest replacementNo / newest createdAt, owner t1224u — with the newest image
 * that passed every check and the saved caption),
 * `GET /images/:t/batches/:id` (the request's image batch status) and `GET /images/:t/telegram-publications` (today's post, manual OR automatic — owner t1200u).
 * The Telegram routes are owner / operator only: a 403 hides the bar; 404 / 503 shows "not available".
 */

const REFRESH_MS = 15_000;

/** The daily flow, in order: 10:00 NY intake starts -> a source post is picked -> the image is made -> an image passes every check -> EMMA saves the caption ->
 *  19:00 NY publish -> published. */
export const TELEGRAM_STEPS = ["Start", "Source post", "Image", "Checks", "Caption", "Publish", "Finished"] as const;
type TgStep = (typeof TELEGRAM_STEPS)[number];

interface ScheduleView { readonly timeZone: string; readonly today: string; readonly nextIntakeAt: string; readonly nextPublishAt: string }
interface RunView { readonly localDate: string; readonly status: string; readonly error: string | null; readonly startedAt: string; readonly finishedAt?: string | null; readonly trigger?: string }
export interface TgStatus {
  readonly schedule?: ScheduleView;
  readonly enabled: boolean;
  readonly autoPublishEnabled: boolean;
  readonly channels: readonly { readonly key: string; readonly title: string; readonly autoPublishEnabled: boolean }[];
  readonly intake?: { readonly enabled: boolean; readonly lastRun: RunView | null };
}
export interface TgRequest {
  readonly id?: string; readonly channelKey: string; readonly batchId: string; readonly localDate: string; readonly captionText?: string | null; readonly imageUrls?: readonly string[];
  /** 0 = the day's first request, 1..2 = same-day replacements (FIX-067 R3.12). */
  readonly replacementNo?: number;
  /** When the request was created (ISO). */
  readonly createdAt?: string;
}
interface TgPost { readonly requests: readonly TgRequest[] }
export interface TgPublication { readonly channel: { readonly key: string }; readonly trigger: string; readonly autoLocalDate: string | null; readonly status: string; readonly error: string | null; readonly createdAt: string; readonly sentAt: string | null; readonly confirmedAt?: string | null }

/** The statuses that occupy the channel's daily slot (the server's countLiveOnLocalDate / liveAttemptIds rule). */
const LIVE_STATUSES = new Set(["sending", "sent", "partially_sent", "uncertain"]);

/** The calendar date (YYYY-MM-DD) of an ISO instant in `timeZone` (the New York day). */
export function localDateIn(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}

/**
 * Owner t1200u: today's publication of a channel = the SAME rule as the server's daily slot (countLiveOnLocalDate): a live publication (sending / sent /
 * partially sent / uncertain), MANUAL or AUTOMATIC, confirmed on the New York day (or the automatic row of that day). Otherwise today's automatic row in
 * any other state (e.g. skipped / caption_missing), so its problem still shows. Null when none.
 */
export function todaysPublication(pubs: readonly TgPublication[], channelKey: string, day: string, timeZone: string): TgPublication | null {
  const mine = pubs.filter((p) => p.channel.key === channelKey);
  const onDay = (p: TgPublication) => p.autoLocalDate === day || [p.confirmedAt, p.sentAt].some((t) => typeof t === "string" && t !== "" && localDateIn(t, timeZone) === day);
  return mine.find((p) => LIVE_STATUSES.has(p.status) && onDay(p)) ?? mine.find((p) => p.trigger === "auto" && p.autoLocalDate === day) ?? null;
}

/**
 * Owner t1224u (R3.12): the bar shows only the channel's LATEST request of the New York day (the one running now); earlier failed ones are ignored. Latest =
 * highest replacementNo, then newest createdAt — NOT the source-post order of `telegram-intake/posts` (a replacement can be made on an older stored post).
 */
export function latestRequestOfDay(requests: readonly TgRequest[], channelKey: string, day: string | null): TgRequest | null {
  if (day === null) return null;
  const t = (r: TgRequest): number => { const v = r.createdAt ? Date.parse(r.createdAt) : NaN; return Number.isNaN(v) ? -Infinity : v; };
  let best: TgRequest | null = null;
  for (const r of requests) {
    if (r.channelKey !== channelKey || r.localDate !== day) continue;
    if (!best || (r.replacementNo ?? 0) > (best.replacementNo ?? 0) || ((r.replacementNo ?? 0) === (best.replacementNo ?? 0) && t(r) > t(best))) best = r;
  }
  return best;
}

export interface TgChannelModel { readonly key: string; readonly title: string; readonly step: TgStep; readonly state: "waiting" | "active" | "done" | "problem" }

/** Pure: one channel's place in today's flow (tested directly). `batchStatus` is the request's image batch status (null = not loaded). */
export function buildTelegramChannelModel(
  channel: TgStatus["channels"][number], status: TgStatus, request: TgRequest | null, batchStatus: string | null, publication: TgPublication | null,
): TgChannelModel {
  const base = { key: channel.key, title: channel.title };
  const s = status.schedule;
  if (publication) {
    if (publication.status === "sent") return { ...base, step: "Finished", state: "done" };
    if (publication.status === "partially_sent") return { ...base, step: "Finished", state: "problem" };
    return { ...base, step: "Publish", state: publication.status === "sending" || publication.status === "preview" ? "active" : "problem" };
  }
  if (!request) {
    const run = status.intake?.lastRun;
    if (run && s && run.localDate === s.today) {
      if (run.status === "running") return { ...base, step: "Source post", state: "active" };
      return { ...base, step: "Source post", state: "problem" };
    }
    return { ...base, step: "Start", state: "waiting" };
  }
  const images = request.imageUrls ?? [];
  if (images.length > 0) {
    if (!request.captionText) return { ...base, step: "Caption", state: "active" };
    return { ...base, step: "Publish", state: "waiting" };
  }
  if (batchStatus === null || batchStatus === "queued" || batchStatus === "running") return { ...base, step: "Image", state: "active" };
  return { ...base, step: "Checks", state: "problem" };
}

/**
 * Owner t1218u: is the channel's daily run in progress (= the video bar's "run active")? From the 10:00 NY intake until the 19:00 NY post: the intake is running,
 * the image is being made, a caption is awaited, or the caption is saved and the post is waiting / sending. NOT in progress: before today's intake (Start), and
 * once the day ended (Finished, or any problem: no source post, no image passed the checks, the post was skipped / failed / unclear).
 */
export function channelRunActive(m: TgChannelModel): boolean {
  return m.state === "active" || (m.state === "waiting" && m.step !== "Start");
}

/** The video bar's time rule (ReviewsAutoRunBar.idleText): HH:MM in Israel time, with the Israel date in front when it is not today. */
export function lastRunWhen(iso: string, now: Date = new Date()): string {
  const day = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jerusalem" }).format(d);
  const hm = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Jerusalem", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  return `${day(new Date(iso)) === day(now) ? "" : `${day(new Date(iso))} `}${hm}`;
}

/** The intake run's trigger in plain words (the video bar's TRIGGER map style). */
const RUN_TRIGGER: Record<string, string> = { schedule: "Daily run", manual: "Run now", replacement: "Replacement run" };

/** Statuses that END a publication (a manual preview / cancelled draft is not a run). */
const ENDED_STATUSES = new Set(["sent", "partially_sent", "failed", "uncertain", "skipped"]);
const endedText = (status: string): string => (status === "sent" ? "finished" : status === "partially_sent" ? "finished with gaps" : status === "uncertain" ? "unclear" : "failed");

/**
 * Pure: the channel's idle line, in the video bar's form (`idleText`): "No automatic run active" + "Last run: <what> · <result> <when>" (null = no last run known).
 * Last run = today's run when it ended with a problem and no publication (time = the intake run's finish), else the channel's newest ended publication
 * (automatic = "Daily run", manual = "Manual post"; time = sent / confirmed / created).
 */
export function telegramIdleText(m: TgChannelModel, channelKey: string, status: TgStatus, pubs: readonly TgPublication[], todays: TgPublication | null, now: Date = new Date()): { readonly line: string; readonly last: string | null } {
  const line = "No automatic run active";
  const run = status.intake?.lastRun;
  if (m.state === "problem" && !todays && run && run.localDate === status.schedule?.today) {
    return { line, last: `Last run: ${RUN_TRIGGER[run.trigger ?? "schedule"] ?? "Daily run"} · failed ${lastRunWhen(run.finishedAt ?? run.startedAt, now)}` };
  }
  const ended = pubs.filter((p) => p.channel.key === channelKey && ENDED_STATUSES.has(p.status));
  const when = (p: TgPublication): string => p.sentAt ?? p.confirmedAt ?? p.createdAt;
  const lastPub = (todays && ENDED_STATUSES.has(todays.status) ? todays : null) ?? [...ended].sort((a, b) => Date.parse(when(b)) - Date.parse(when(a)))[0] ?? null;
  if (!lastPub) return { line, last: null };
  return { line, last: `Last run: ${lastPub.trigger === "auto" ? "Daily run" : "Manual post"} · ${endedText(lastPub.status)} ${lastRunWhen(when(lastPub), now)}` };
}

export interface TgChannelView { readonly model: TgChannelModel; readonly active: boolean; readonly idle: { readonly line: string; readonly last: string | null } }

export function ReviewsTelegramBar({ tenantId }: { readonly tenantId: string }) {
  const [rows, setRows] = useState<readonly TgChannelView[] | null>(null);
  const [unavailable, setUnavailable] = useState<"none" | "hidden" | "unavailable">("none");
  const off = useRef(false);

  useEffect(() => {
    let live = true;
    off.current = false;
    const base = `/images/${tenantId}`;
    const load = async (): Promise<void> => {
      if (off.current) return;
      try {
        const status = await api.get<TgStatus>(`${base}/telegram-publish/status`);
        if (!live || !status) return;
        const day = status.schedule?.today ?? null;
        const [posts, pubs] = await Promise.all([
          api.get<{ posts: readonly TgPost[] }>(`${base}/telegram-intake/posts?limit=200`).catch(() => null),
          api.get<{ publications: readonly TgPublication[] }>(`${base}/telegram-publications?limit=50`).catch(() => null),
        ]);
        const requests = (posts?.posts ?? []).flatMap((p) => p.requests);
        const models = await Promise.all(status.channels.map(async (ch) => {
          const request = latestRequestOfDay(requests, ch.key, day);
          const publication = day === null ? null : todaysPublication(pubs?.publications ?? [], ch.key, day, status.schedule?.timeZone ?? "America/New_York");
          let batchStatus: string | null = null;
          if (request && !publication && (request.imageUrls ?? []).length === 0) {
            try { batchStatus = (await api.get<{ batch: { status: string } }>(`${base}/batches/${encodeURIComponent(request.batchId)}`))?.batch?.status ?? null; } catch { /* shown as still making the image */ }
          }
          const model = buildTelegramChannelModel(ch, status, request, batchStatus, publication);
          return { model, active: channelRunActive(model), idle: telegramIdleText(model, ch.key, status, pubs?.publications ?? [], publication) };
        }));
        if (!live) return;
        setUnavailable("none"); setRows(models);
      } catch (e) {
        if (live && e instanceof ApiError && (e.status === 403 || e.status === 503 || e.status === 404)) { off.current = true; setRows(null); setUnavailable(e.status === 403 ? "hidden" : "unavailable"); }
        else if (live) setUnavailable((u) => (u === "hidden" ? u : "unavailable"));
      }
    };
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => { live = false; clearInterval(t); };
  }, [tenantId]);

  if (unavailable === "hidden") return null;
  if (!rows) {
    return (
      <div className="card auto-run-bar" data-testid="reviews-telegram-bar" data-state="idle" role="status" aria-live="polite">
        <strong data-testid="reviews-telegram-bar-headline">{unavailable === "unavailable" ? "Telegram daily publish status is not available" : "Checking the Telegram daily publish…"}</strong>
      </div>
    );
  }
  // Owner t1218u: one card per channel, each one exactly like the video bar (idle line / step strip while running).
  return (
    <div data-testid="reviews-telegram-bar" data-state="ready">
      {rows.map(({ model: r, active, idle }) => (
        active ? (
          <div key={r.key} className="card auto-run-bar" data-testid={`reviews-telegram-bar-${r.key}`} data-state={r.state} role="status" aria-live="polite">
            <strong data-testid={`reviews-telegram-bar-headline-${r.key}`}>{r.title}</strong>
            <StepStrip steps={TELEGRAM_STEPS} current={TELEGRAM_STEPS.indexOf(r.step)} testId={`reviews-telegram-bar-steps-${r.key}`} />
          </div>
        ) : (
          <div key={r.key} className="card auto-run-bar" data-testid={`reviews-telegram-bar-${r.key}`} data-state="idle" role="status" aria-live="polite">
            <strong data-testid={`reviews-telegram-bar-headline-${r.key}`}>{r.title} · {idle.line}</strong>
            {idle.last ? <span className="muted" data-testid={`reviews-telegram-bar-last-${r.key}`} style={{ marginLeft: 12 }}>{idle.last}</span> : null}
          </div>
        )
      ))}
    </div>
  );
}
